/**
 * Alert Component
 *
 * Displays persistent inline messages for errors, warnings, info, and success states.
 * Different from Toast which shows temporary notifications.
 *
 * @example
 * ```tsx
 * <Alert variant="error" title="Error">
 *   Your form could not be submitted. Please check the fields and try again.
 * </Alert>
 *
 * <Alert variant="warning" dismissible onDismiss={() => setVisible(false)}>
 *   Your session will expire in 5 minutes.
 * </Alert>
 * ```
 */

import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { CheckCircle2, AlertCircle, AlertTriangle, Info, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/cn';

const alertVariants = cva(
  'relative w-full rounded-2xl border px-4 py-3 text-sm [&>svg]:absolute [&>svg]:inset-inline-start-4 [&>svg]:top-4 [&>svg+div]:ps-8',
  {
    variants: {
      variant: {
        default: 'bg-bg-secondary border-border-primary text-text-primary',
        info: 'bg-brand-primary/10 border-brand-primary/40 text-brand-primary [&>svg]:text-brand-primary',
        success:
          'bg-success-500/10 border-success-500/40 text-success-900 dark:text-success-300 [&>svg]:text-success-500',
        warning:
          'bg-warning-500/10 border-warning-500/40 text-warning-900 dark:text-warning-300 [&>svg]:text-warning-500',
        error:
          'bg-error-500/10 border-error-500/40 text-error-900 dark:text-error-300 [&>svg]:text-error-500',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

const iconMap = {
  default: Info,
  info: Info,
  success: CheckCircle2,
  warning: AlertTriangle,
  error: AlertCircle,
} as const;

export interface AlertProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof alertVariants> {
  /** Alert title (optional) */
  title?: string;
  /** Whether alert can be dismissed */
  dismissible?: boolean;
  /** Callback when dismissed */
  onDismiss?: () => void;
  /** Custom icon (overrides default) */
  icon?: React.ReactNode;
  /** Hide default icon */
  hideIcon?: boolean;
}

/**
 * Alert component for persistent inline messages
 * @param variant - Alert style (default, info, success, warning, error)
 * @param title - Optional title text
 * @param dismissible - Show close button
 * @param onDismiss - Callback when dismissed
 * @param icon - Custom icon element
 * @param hideIcon - Hide the default icon
 */
export const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  (
    {
      className,
      variant,
      title,
      dismissible,
      onDismiss,
      icon,
      hideIcon,
      children,
      ...props
    },
    ref
  ) => {
    const { t } = useTranslation('common');
    const [isVisible, setIsVisible] = React.useState(true);
    const Icon = iconMap[variant || 'default'];

    const handleDismiss = () => {
      setIsVisible(false);
      onDismiss?.();
    };

    if (!isVisible) return null;

    return (
      <div
        ref={ref}
        role="alert"
        className={cn(alertVariants({ variant }), className)}
        {...props}
      >
        {!hideIcon && (icon || <Icon className="h-5 w-5" strokeWidth={1.5} aria-hidden="true" />)}
        <div className={cn(!hideIcon && 'ps-8')}>
          {title && (
            <h5 className="mb-1 font-semibold leading-none tracking-tight">
              {title}
            </h5>
          )}
          <div className="text-sm leading-relaxed [&_p]:leading-relaxed">
            {children}
          </div>
        </div>
        {dismissible && (
          <button
            type="button"
            onClick={handleDismiss}
            className="absolute inset-inline-end-3 top-3 rounded-xl p-1 hover:bg-black/5 dark:hover:bg-bg-primary/5 transition-premium focus:outline-none focus:ring-2 focus:ring-brand-primary focus:ring-offset-0"
            aria-label={t('aria.dismissAlert')}
          >
            <X className="h-4 w-4" strokeWidth={1.5} />
          </button>
        )}
      </div>
    );
  }
);

Alert.displayName = 'Alert';

/**
 * AlertTitle Component
 *
 * For use within Alert when you need more control over title styling
 */
export const AlertTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h5
    ref={ref}
    className={cn('mb-1 font-semibold leading-none tracking-tight', className)}
    {...props}
  />
));
AlertTitle.displayName = 'AlertTitle';

/**
 * AlertDescription Component
 *
 * For use within Alert when you need more control over description styling
 */
export const AlertDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('text-sm leading-relaxed [&_p]:leading-relaxed', className)}
    {...props}
  />
));
AlertDescription.displayName = 'AlertDescription';
